import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authApi from '../../api/auth';
import { generateTraceId, logAction } from '../../utils/trace';

// Async thunks
export const fetchDataSources = createAsyncThunk(
  'dataSources/fetchDataSources',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'dataSources/fetchDataSources/pending', meta: { traceId } });
      const data = await authApi.listDataSources({ traceId });
      logAction({ type: 'dataSources/fetchDataSources/fulfilled', meta: { traceId } }, {
        count: data.length,
      });
      return data;
    } catch (error) {
      logAction({ type: 'dataSources/fetchDataSources/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDataSource = createAsyncThunk(
  'dataSources/fetchDataSource',
  async ({ dataSourceId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'dataSources/fetchDataSource/pending', meta: { traceId } }, {
        dataSourceId,
      });
      const data = await authApi.getDataSource(dataSourceId, { traceId });
      logAction({ type: 'dataSources/fetchDataSource/fulfilled', meta: { traceId } }, {
        dataSourceId,
        name: data.name,
      });
      return data;
    } catch (error) {
      logAction({ type: 'dataSources/fetchDataSource/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDataSource = createAsyncThunk(
  'dataSources/deleteDataSource',
  async ({ dataSourceId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'dataSources/deleteDataSource/pending', meta: { traceId } }, {
        dataSourceId,
      });
      await authApi.deleteDataSource(dataSourceId, { traceId });
      logAction({ type: 'dataSources/deleteDataSource/fulfilled', meta: { traceId } }, {
        dataSourceId,
      });
      return dataSourceId;
    } catch (error) {
      logAction({ type: 'dataSources/deleteDataSource/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

const dataSourcesSlice = createSlice({
  name: 'dataSources',
  initialState: {
    items: [],
    selectedDataSource: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDataSource: (state, action) => {
      state.selectedDataSource = action.payload;
    },
    updateDataSourceAlias: (state, action) => {
      const { dataSourceId, alias } = action.payload;
      const item = state.items?.find(ds => ds.data_source_id === dataSourceId);
      if (item) {
        item.alias = alias;
      }
      if (state.selectedDataSource?.data_source_id === dataSourceId) {
        state.selectedDataSource.alias = alias;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDataSources
      .addCase(fetchDataSources.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDataSources.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        // If the currently selected data source is not in the new list (e.g. after
        // a tenant switch), reset selection to the first item in the new list.
        // Also auto-select the first item when nothing is selected yet.
        const currentId = state.selectedDataSource?.data_source_id;
        const stillExists = currentId && action.payload.some(ds => ds.data_source_id === currentId);
        if (!stillExists) {
          state.selectedDataSource = action.payload.length > 0 ? action.payload[0] : null;
        }
      })
      .addCase(fetchDataSources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // fetchDataSource
      .addCase(fetchDataSource.fulfilled, (state, action) => {
        // Update item in list if it exists
        const index = state.items.findIndex(
          ds => ds.data_source_id === action.payload.data_source_id
        );
        if (index >= 0) {
          state.items[index] = action.payload;
        }
        // Update selected if it's the same
        if (state.selectedDataSource?.data_source_id === action.payload.data_source_id) {
          state.selectedDataSource = action.payload;
        }
      })
      // deleteDataSource
      .addCase(deleteDataSource.fulfilled, (state, action) => {
        state.items = state.items.filter(
          ds => ds.data_source_id !== action.payload
        );
        // Clear selection if deleted
        if (state.selectedDataSource?.data_source_id === action.payload) {
          state.selectedDataSource = state.items[0] || null;
        }
      });
  },
});

export const { clearError, setSelectedDataSource, updateDataSourceAlias } = dataSourcesSlice.actions;
export default dataSourcesSlice.reducer;
